import { motion } from "framer-motion";
import { Stethoscope, Activity, ShieldCheck, Leaf, Flower2, Sparkles, Syringe, ArrowUpRight } from "lucide-react";
import { SERVICES } from "../../data/content";
import { Reveal, Eyebrow, scrollToId } from "./Reveal";

const ICONS = { Stethoscope, Activity, ShieldCheck, Leaf, Flower2, Sparkles, Syringe };
const LAYOUT = {
  "general-opd": "md:col-span-2 lg:col-span-2 lg:row-span-2 feature",
  "chronic-care": "md:col-span-2 lg:col-span-2 dark",
  "preventive-health": "",
  "lifestyle-counselling": "",
  "womens-health": "md:col-span-2 lg:col-span-2 warm",
  "skin-hair-care": "",
  "basic-procedures": "",
};

const Card = ({ s, i }) => {
  const Icon = ICONS[s.icon];
  const variant = LAYOUT[s.id];
  const isFeature = variant.includes("feature");
  const isDark = variant.includes("dark");
  const isWarm = variant.includes("warm");
  const tone = isDark ? "bg-[#0E4D44] text-[#F6F4EE]" : isWarm ? "bg-[#F3E2D6] text-[#0B2B26]" : "bg-white text-[#0B2B26]";
  return (
    <motion.article
      data-testid={`service-card-${s.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`bento-card group relative overflow-hidden rounded-[28px] border border-[#0E4D44]/10 p-7 flex flex-col min-h-[240px] ${tone} ${variant.replace(/feature|dark|warm/g, "")}`}
    >
      {isFeature && (
        <>
          <img src="/images/stethoscope.jpg" alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B2B26] via-[#0B2B26]/55 to-[#0B2B26]/5" />
        </>
      )}
      <div className="relative flex items-start justify-between">
        <span className={`h-12 w-12 rounded-2xl grid place-items-center ${isFeature || isDark ? "bg-white/12 text-[#F6F4EE] backdrop-blur" : "bg-[#E6F0EE] text-[#0E4D44]"}`}>
          <Icon className="h-5 w-5" strokeWidth={1.6} />
        </span>
        <span className={`font-mono text-[11px] uppercase tracking-[0.2em] ${isFeature || isDark ? "text-[#CFE0DA]" : "text-[#4A6660]"}`}>{s.tag}</span>
      </div>
      <div className={`relative mt-auto pt-10 ${isFeature ? "text-[#F6F4EE]" : ""}`}>
        <h3 className={`font-display tracking-tight leading-none ${isFeature ? "text-4xl sm:text-5xl" : "text-3xl"}`}>{s.title}</h3>
        <ul className="mt-5 flex flex-wrap gap-2">
          {s.items.map((it) => (
            <li key={it} className={`text-xs sm:text-[13px] rounded-full px-3 py-1.5 border ${isFeature || isDark ? "border-white/25 text-[#E8F0ED]" : "border-[#0E4D44]/15 text-[#22413B]"}`}>{it}</li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="services" className="py-24 sm:py-36 bg-[#EEF1EC]">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <Reveal>
          <Eyebrow>Services</Eyebrow>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#0B2B26] max-w-3xl">
            Seven ways we care, <span className="italic text-[#0E4D44]">one</span> continuous relationship.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <button data-testid="services-book-btn" onClick={() => scrollToId("book")} className="btn-ghost group">
            Book any service <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </Reveal>
      </div>
      <div data-testid="services-bento-grid" className="mt-14 sm:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[minmax(260px,auto)] gap-4 sm:gap-5">
        {SERVICES.map((s, i) => <Card key={s.id} s={s} i={i} />)}
      </div>
    </div>
  </section>
);
